import { format } from 'date-fns/format';
import { de } from 'date-fns/locale/de';

/**
 * Formats a date for display in PDF documents
 * @param date - Date string or Date object
 * @returns Formatted date string in German locale
 */
export function formatPDFDate(date: string | Date | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, "dd. MMMM yyyy", { locale: de });
  } catch {
    return '';
  }
}

/**
 * Formats a date with time for PDF documents
 * @param date - Date string or Date object
 * @returns Formatted date and time string in German locale
 */
export function formatPDFDateTime(date: string | Date | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, "dd. MMMM yyyy 'um' HH:mm 'Uhr'", { locale: de });
  } catch {
    return '';
  }
}

/**
 * Generates a filename for PDF downloads
 * @param title - Article or document title
 * @param suffix - Optional suffix (e.g., 'artikel', 'bericht')
 * @returns Sanitized filename with timestamp
 */
export function generatePDFFilename(title: string, suffix?: string): string {
  const timestamp = format(new Date(), 'yyyy-MM-dd');
  const sanitizedTitle = title
    .toLowerCase()
    .replace(/[äöüß]/g, (match) => {
      const map: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
      return map[match] || match;
    })
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
  
  const baseName = suffix ? `${sanitizedTitle}-${suffix}` : sanitizedTitle;
  return `${baseName}_${timestamp}.pdf`;
}

/**
 * Sanitizes text content for PDF rendering
 * Removes HTML tags and decodes common HTML entities
 * @param content - Raw content string (may contain HTML)
 * @returns Clean text content
 */
export function sanitizePDFContent(content: string): string {
  if (!content) return '';
  
  return content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * PDF page sizes in points (1 inch = 72 points)
 */
export const PDF_PAGE_SIZES = {
  A4: { width: 595.28, height: 841.89 },
  LETTER: { width: 612, height: 792 },
} as const;

/**
 * PDF margins in points
 */
export const PDF_MARGINS = {
  normal: { top: 72, right: 72, bottom: 72, left: 72 },
  narrow: { top: 54, right: 54, bottom: 54, left: 54 },
  wide: { top: 90, right: 90, bottom: 90, left: 90 },
} as const;

/**
 * Observatory brand colors for PDF documents
 */
export const PDF_COLORS = {
  primary: '#1e3a5f',      // Dark blue (header)
  secondary: '#4c1d95',    // Purple accent
  accent: '#3b82f6',       // Blue accent
  text: '#1f2937',         // Dark gray (main text)
  textLight: '#4b5563',    // Medium gray (secondary text)
  textMuted: '#6b7280',    // Light gray (muted text)
  background: '#ffffff',   // White background
  border: '#e5e7eb',       // Light gray border
} as const;

/**
 * PDF font sizes
 */
export const PDF_FONTS = {
  title: 24,
  subtitle: 14,
  heading: 16,
  body: 11,
  small: 9,
  caption: 8,
} as const;
